import { useEffect, useRef } from "react";
import {
  useHelpCenterStore,
  translateHelpCenterText,
} from "../../../Stores/HelpCenter";
import { useRouterStore } from "../../../Stores/Router";
import { useConfigStore } from "../../../Stores/Config";
import { ReactComponent as GleapBotIcon } from "../../../Assets/GleapBotIcon.svg";
import { AvatarImage } from "../../../Components/AvatarImage/AvatarImage";
import { replaceMarkdown } from "../../../Helper/MarkdownReplace";
import { sanitizeRichHtml } from "../../../Helper/sanitize";
import Communicator from "../../../Helper/Communicator";
import { helpCenterDefaultHost } from "../../../Helper/HelpCenterUrl";
import { ChevronRight } from "../HelpCenterShared/HelpCenterShared";
import { activateOnKey } from "../../../Helper/a11y";
import "./HelpCenterAISearch.scss";

// "Enforce EU AI Act": the answer is always labelled as AI — "AI Response ·
// Kai" — and a blank custom wording can't remove the label.
const getEuAiActAimark = (botName: string) => {
  const label = translateHelpCenterText("aiResponse", "aiResponse", "AI Response");
  const aiLabel = label.trim().length > 0 ? label : "AI Response";
  return botName ? `${aiLabel} · ${botName}` : aiLabel;
};

function BotIcon() {
  const operatorAvatarImage = useConfigStore(
    (state) => state.config?.operatorAvatarImage,
  );

  return (
    <div className="helpcenter-ai-bot-icon">
      {operatorAvatarImage ? (
        <AvatarImage
          src={operatorAvatarImage}
          alt=""
          fallback={<GleapBotIcon />}
        />
      ) : (
        <GleapBotIcon />
      )}
    </div>
  );
}

/**
 * A cited source: help center articles open in the widget; any other source Kai
 * cited (a crawled page, a snippet's link) opens its URL.
 */
const openSource = (source: { id: string; url?: string; sourceType?: string }) => {
  if (source.sourceType && source.sourceType !== "article" && source.url) {
    Communicator.openURL(source.url, true, { placeholders: false });
    return;
  }
  useHelpCenterStore.getState().openArticle(source.id);
};

export function AISearchResults() {
  const aiAnswer = useHelpCenterStore((s) => s.aiAnswer);
  const aiSources = useHelpCenterStore((s) => s.aiSources);
  const aiLoading = useHelpCenterStore((s) => s.aiLoading);
  const configStore = useConfigStore();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a");
      if (!anchor) return;

      e.preventDefault();
      e.stopPropagation();

      const href = anchor.getAttribute("href");
      if (!href) return;

      try {
        const url = new URL(href);
        const config = useConfigStore.getState().config;
        const defaultDomain = config?.defaultDomainHelpCenter
          ? helpCenterDefaultHost(config)
          : null;
        const customDomain = config?.customDomainHelpCenter || null;

        const isHelpCenterUrl =
          (defaultDomain && url.hostname === defaultDomain) ||
          (customDomain && url.hostname === customDomain);

        if (isHelpCenterUrl) {
          const segments = url.pathname.split("/").filter(Boolean);
          const artIdx = segments.indexOf("articles");
          if (artIdx !== -1 && artIdx + 1 < segments.length) {
            const docId = segments[artIdx + 1].split("-")[0];
            if (docId) {
              useHelpCenterStore.getState().openArticle(docId);
              return;
            }
          }
        }
      } catch {}

      Communicator.openURL(href, true, { placeholders: false });
    };

    container.addEventListener("click", handleClick, true);
    return () => container.removeEventListener("click", handleClick, true);
  }, [aiAnswer]);

  return (
    <div className="helpcenter-ai-results" ref={contentRef}>
      <div className="helpcenter-ai-results-answer-container">
        <BotIcon />
        <div className="helpcenter-ai-results-answer-wrapper">
          {aiLoading ? (
            <div className="helpcenter-ai-results-answer helpcenter-ai-results-answer-loading">
              <div className="helpcenter-ai-dot-flashing" />
            </div>
          ) : aiAnswer && aiAnswer.length > 0 ? (
            <div
              className="helpcenter-ai-results-answer"
              dangerouslySetInnerHTML={{
                __html: sanitizeRichHtml(replaceMarkdown(aiAnswer)),
              }}
            />
          ) : (
            // The dots used to be gated on !aiAnswer, so "sources succeeded,
            // answer request failed" flashed them forever. Once loading has
            // settled without an answer, say so instead.
            <div className="helpcenter-ai-results-answer">
              {configStore.translateTextWithFallback(
                "aiAnswerUnavailable",
                "Sorry, I couldn't generate an answer right now. Please try again.",
              )}
            </div>
          )}
          <div className="helpcenter-ai-results-aimark">
            {configStore.isEuAiActEnforced()
              ? getEuAiActAimark(configStore.getBotName())
              : configStore.getBotName() ||
                translateHelpCenterText("aiResponse", "aiResponse", "AI Response")}
          </div>
        </div>
      </div>

      {aiSources && aiSources.length > 0 && (
        <div className="helpcenter-ai-results-sources">
          {aiSources.map(
            (
              source: {
                title: string;
                extract?: string;
                id: string;
                url: string;
                sourceType?: string;
              },
              index: number,
            ) => (
              <div
                key={source.id ?? index}
                className="helpcenter-ai-results-sources-item"
                role="button"
                tabIndex={0}
                onClick={() => openSource(source)}
                onKeyDown={activateOnKey(() => openSource(source))}
              >
                <div className="helpcenter-ai-results-sources-item-details">
                  <div className="helpcenter-ai-results-sources-item-details-title">
                    {source.title}
                  </div>
                  {source.extract && (
                    <div className="helpcenter-ai-results-sources-item-details-description">
                      {source.extract}
                    </div>
                  )}
                </div>
                <ChevronRight />
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}
